'use client';

import React from 'react';
import { Box, Typography } from '@mui/material';
import { Award, CalendarCheck, Shield, TrendingUp, Zap } from 'lucide-react';

import { useT } from '@/hooks';
import { colors } from '@/styles/theme';

interface Step {
  icon: React.ElementType;
  color: string;
  bg: string;
  titleKey: string;
  titleDefault: string;
  bodyKey: string;
  bodyDefault: string;
}

const STEPS: Step[] = [
  {
    icon: CalendarCheck,
    color: '#e8b046',
    bg: 'rgba(249,115,22,0.12)',
    titleKey: 'streaks.step1Title',
    titleDefault: 'Trade daily to build your streak',
    bodyKey: 'streaks.step1Body',
    bodyDefault:
      "Hit that day's trading volume target and your streak extends by one day. The target starts at $10 and climbs through the week.",
  },
  {
    icon: TrendingUp,
    color: '#a855f7',
    bg: 'rgba(168,85,247,0.12)',
    titleKey: 'streaks.step2Title',
    titleDefault: 'Earn points every day you qualify',
    bodyKey: 'streaks.step2Body',
    bodyDefault:
      'Get 50 points for every day you hit your target, plus a 500-point bonus on day 7 of each cycle.',
  },
  {
    icon: Shield,
    color: colors.info.main,
    bg: 'rgba(91, 140, 255, 0.12)',
    titleKey: 'streaks.step3Title',
    titleDefault: 'Miss a day? A freeze has you covered',
    bodyKey: 'streaks.step3Body',
    bodyDefault:
      'Spend 200 points on a freeze and it automatically protects your streak the next time you miss a target — no streak lost.',
  },
  {
    icon: Award,
    color: '#f5b301',
    bg: 'rgba(245,179,1,0.12)',
    titleKey: 'streaks.step4Title',
    titleDefault: 'Unlock badges for good',
    bodyKey: 'streaks.step4Body',
    bodyDefault:
      'Hit 7, 30, 100, 180, 365, 500, or 1000 consecutive days and that badge is yours permanently — even if your streak later resets.',
  },
  {
    icon: Zap,
    color: '#4ec38a',
    bg: 'rgba(78, 195, 138, 0.12)',
    titleKey: 'streaks.step5Title',
    titleDefault: 'Climb the leaderboard',
    bodyKey: 'streaks.step5Body',
    bodyDefault:
      "Ranked live by your current streak, not your best one — so the leaderboard always shows who's on a run right now.",
  },
];

/**
 * StreakHowItWorks
 * Plain-language walkthrough of the mechanic — the whole reason this page
 * exists instead of a widget buried in Portfolio. Five steps, each phrased
 * around what the trader does and gets, not the underlying data model.
 */
export const StreakHowItWorks: React.FC = () => {
  const { t } = useT();

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
        {t('streaks.howItWorksTitle', { defaultValue: 'How streaks work' })}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: 620 }}>
        {t('streaks.howItWorksSubtitle', {
          defaultValue: "Five rules — that's the whole system.",
        })}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, paddingX: 2 }}>
        {STEPS.map((step, index) => (
          <Box
            key={step.titleKey}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              py: 2.25,
              borderBottom: index < STEPS.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2.5,
                bgcolor: step.bg,
                color: step.color,
              }}
            >
              <step.icon size={19} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.25 }}>
                {t(step.titleKey, { defaultValue: step.titleDefault })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                {t(step.bodyKey, { defaultValue: step.bodyDefault })}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
